import { UploadOutlined } from '@ant-design/icons';
import { Button, Select, Switch, Upload } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { useMemo, useState } from 'react';

import BVContainerLoader from '../../BVContainerLoader/bv-container-loader';
import BVProductsConfirmDeleteModal from './bv-products-confirm-delete-modal';

type BusinessUnit = 'watches' | 'jewels';

type BVProductsImportProps = {
	fileList: UploadFile[];
	setFileList: (list: UploadFile[]) => void;
	uploadProductsExcel: (
		file: File,
		businessUnit: BusinessUnit,
		skipRemove?: boolean,
	) => Promise<void>;
	loading: boolean;
	error: string | null;
	resetProductsUploadState: () => void;
};

const BVProductsImport = ({
	fileList,
	setFileList,
	uploadProductsExcel,
	loading,
	error,
	resetProductsUploadState,
}: BVProductsImportProps) => {
	const [businessUnit, setBusinessUnit] = useState<BusinessUnit>('watches');
	const [skipRemove, setSkipRemove] = useState<boolean>(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const disableActions = useMemo(
		() => loading || fileList.length === 0,
		[loading, fileList.length],
	);

	const beforeUploadProducts = (file: RcFile) => {
		if (!file.name.endsWith('.csv')) {
			return Upload.LIST_IGNORE;
		}

		resetProductsUploadState();

		setFileList([
			{
				uid: file.uid,
				name: file.name,
				status: 'done',
				originFileObj: file,
			},
		]);

		return false;
	};

	const doImport = async () => {
		const f = fileList[0]?.originFileObj;
		if (!f) return;
		await uploadProductsExcel(f as File, businessUnit, skipRemove);
	};

	const onClickImport = async () => {
		if (!skipRemove) {
			setConfirmOpen(true);
			return;
		}
		await doImport();
	};

	const onConfirm = async () => {
		setConfirmOpen(false);
		await doImport();
	};

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div className="admin-row-3">
				<label className="admin-label">Import Products</label>

				<div
					style={{
						display: 'flex',
						gap: 12,
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<Upload
						beforeUpload={beforeUploadProducts}
						fileList={fileList}
						maxCount={1}
						onRemove={() => setFileList([])}
						accept=".csv"
					>
						<Button icon={<UploadOutlined />}>Select File</Button>
					</Upload>
					<span>Business unit</span>
					<Select<BusinessUnit>
						value={businessUnit}
						style={{ width: 160 }}
						onChange={setBusinessUnit}
						options={[
							{ value: 'watches', label: 'Watches' },
							{ value: 'jewels', label: 'Jewels' },
						]}
					/>

					<span>Skip Remove</span>
					<Switch checked={skipRemove} onChange={setSkipRemove} />
				</div>

				<Button
					type="primary"
					danger={!skipRemove}
					onClick={onClickImport}
					disabled={disableActions}
				>
					Upload file
				</Button>

				{error && <div className="admin-row-3__full admin-error">{error}</div>}
				<BVContainerLoader loading={loading} />
			</div>

			<BVProductsConfirmDeleteModal
				open={confirmOpen}
				content="With 'Skip Remove' disabled, existing products not present in the excel will be deleted before importing. Do you want to continue?"
				loading={loading}
				onCancel={() => setConfirmOpen(false)}
				onConfirm={onConfirm}
			/>
		</div>
	);
};

export default BVProductsImport;
